import React, { useEffect, useId, useState } from 'react';
import { useStyletron } from 'baseui';
import { Check } from 'baseui/icon';
import { Icon } from '@relaynetwork/design-system';
import { KIND as ButtonKind, SIZE as ButtonSize } from 'baseui/button';
import { CustomTheme } from '../baseweb-custom-theme/customTheme';
import { Block } from 'baseui/block';
import { ModalButton, ModalFooter } from 'baseui/modal';
import { Select, Value } from 'baseui/select';
import { Textarea } from 'baseui/textarea';
import { ClosedReasons } from '@app/two-way/setup/components/setup-conversations/components/closed-reasons-axios-service/closed-reasons-axios-service';
import { SupportedLanguage } from '../relay-messenger-axios-service/relay-messenger-axios-service';

export type ReasonsSelector = {
  kind: 'reasons';
  options: ClosedReasons[];
};

export type LanguagesSelector = {
  kind: 'languages';
  options: SupportedLanguage[];
};

// each variant carries its own Select config (label/value keys, required/searchable
// behavior, test ids), so the JSX below never has to branch on data shape
const SELECT_CONFIG_BY_KIND = {
  reasons: {
    labelKey: 'reason',
    valueKey: 'id',
    required: true,
    clearable: true,
    searchable: true,
    dataTestId: 'select-close-reason',
    optionTestId: 'close-reasons',
  },
  languages: {
    labelKey: 'name',
    valueKey: 'code',
    required: false,
    clearable: false,
    searchable: false,
    dataTestId: 'select-ping-language',
    optionTestId: 'ping-language-option',
  },
} as const;

export type ActionsModalResult =
  | {
      kind: 'reasons';
      reasonId: string | null;
      reasonName: string | null;
      reasonText: string;
    }
  | { kind: 'languages'; language?: string };

export const isReasonsResult = (
  result?: ActionsModalResult,
): result is Extract<ActionsModalResult, { kind: 'reasons' }> =>
  result?.kind === 'reasons';

export const isLanguagesResult = (
  result?: ActionsModalResult,
): result is Extract<ActionsModalResult, { kind: 'languages' }> =>
  result?.kind === 'languages';

export type ActionsModalProps = {
  closeModal: () => void;
  successModal: (result?: ActionsModalResult) => void;
  modalSubTitle: React.ReactNode;
  cancelBtnText: string;
  successBtnText: string;
  dataTestId?: string;
  selectLabel?: string;
  selector?: ReasonsSelector | LanguagesSelector;
  selectorErrorMsg?: string;
  onSelectorRetry?: () => void;
  onSelectorErrorDismiss?: () => void;
};
export const ActionsModal = ({
  closeModal,
  successModal,
  modalSubTitle,
  cancelBtnText,
  successBtnText,
  dataTestId,
  selectLabel,
  selector,
  selectorErrorMsg,
  onSelectorRetry,
  onSelectorErrorDismiss,
}: ActionsModalProps) => {
  const [css, theme] = useStyletron();
  const customTheme = theme as CustomTheme;
  const [selectedValue, setSelectedValue] = useState<Value>([]);
  const [showTextArea, setShowTextArea] = useState(false);
  const selectOptions = selector?.options ?? [];
  const selectConfig = SELECT_CONFIG_BY_KIND[selector?.kind ?? 'reasons'];
  // Closed reasons state
  const [reasonId, setReasonId] = useState<string | null>(null);
  const [reasonName, setReasonName] = useState<string | null>(null);
  const [reasonText, setReasonText] = useState('');
  // Language selector state
  const isLanguageSelector = selector?.kind === 'languages';
  const selectLabelId = useId();

  useEffect(() => {
    if (
      isLanguageSelector &&
      !selectorErrorMsg &&
      selectOptions.length > 0 &&
      selectedValue.length === 0
    ) {
      setSelectedValue([selectOptions[0]]); // the first language is the default selection for pinging a customer
    }
  }, [isLanguageSelector, selectorErrorMsg, selectOptions, selectedValue]);

  useEffect(() => {
    if (selector?.kind !== 'reasons' || selectedValue.length !== 1) {
      return;
    }
    const selectedReason = selectedValue[0];
    if (selectedReason && selectedReason.allowAdditionalDetails) {
      setShowTextArea(true);
      setReasonText('');
    } else if (selectedReason && !selectedReason.allowAdditionalDetails) {
      setShowTextArea(false);
    }
    setReasonId(selectedReason.id != null ? String(selectedReason.id) : null);
    setReasonName(selectedReason.reason ?? null);
  }, [selectedValue, selector]);

  return (
    <Block>
      <div
        className={css({
          fontWeight: '400',
          color: '#37474F',
          paddingLeft: '10px',
        })}
      >
        {modalSubTitle}
        {selectOptions.length > 0 && (
          <div className={css({ marginTop: '20px' })}>
            {selectLabel && (
              <div
                id={selectLabelId}
                className={css({ fontWeight: '600', marginBottom: '10px' })}
              >
                {selectLabel}
              </div>
            )}
            <Select
              options={selectOptions}
              labelKey={selectConfig.labelKey}
              valueKey={selectConfig.valueKey}
              required={selectConfig.required}
              clearable={selectConfig.clearable}
              searchable={selectConfig.searchable}
              aria-labelledby={selectLabel ? selectLabelId : undefined}
              error={!!selectorErrorMsg}
              placeholder={selectorErrorMsg ? 'Could not load' : undefined}
              onChange={({ value }) => {
                setSelectedValue(value);
                if (selectorErrorMsg) {
                  onSelectorErrorDismiss?.();
                }
              }}
              value={selectedValue}
              overrides={{
                Root: {
                  props: {
                    'data-testid': selectConfig.dataTestId,
                  },
                },
                OptionContent: {
                  props: {
                    'data-testid': selectConfig.optionTestId,
                  },
                },
                ControlContainer: {
                  style: {
                    color: '#3693bf',
                  },
                },
                SelectArrow: {
                  props: {
                    overrides: {
                      Svg: {
                        style: {
                          color: '#3693bf',
                        },
                      },
                    },
                  },
                },
              }}
            />
            {selectorErrorMsg && (
              <div
                className={css({
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginTop: '8px',
                  color: customTheme.colors.inputBorderError,
                  fontSize: '14px',
                })}
                data-testid="selector-error-message"
              >
                <Icon
                  name="Error"
                  size="small"
                  color={customTheme.colors.inputBorderError}
                />
                <span>{selectorErrorMsg}</span>
                {onSelectorRetry && (
                  <button
                    type="button"
                    onClick={onSelectorRetry}
                    className={css({
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      fontWeight: '800',
                      color: '#3693bf',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    })}
                  >
                    Try again
                  </button>
                )}
              </div>
            )}
            {showTextArea && (
              <div className={css({ marginTop: '20px' })}>
                <Textarea
                  value={reasonText}
                  onChange={(e) => setReasonText(e.currentTarget.value)}
                  maxLength={256}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <ModalFooter
        className={css({
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
          alignItems: 'center',
        })}
      >
        <Block
          className={css({
            display: 'flex',
            gap: '10px',
          })}
        >
          <ModalButton
            kind={ButtonKind.tertiary}
            size={ButtonSize.mini}
            autoFocus={false}
            overrides={{
              BaseButton: {
                style: {
                  height: '40px',
                  color: '#3693bf',
                  fontWeight: '400',
                },
              },
            }}
            onClick={closeModal}
          >
            {cancelBtnText}
          </ModalButton>
          <ModalButton
            size={ButtonSize.mini}
            kind={ButtonKind.secondary}
            endEnhancer={
              <div className={css({ display: 'flex', alignItems: 'center' })}>
                <div
                  className={css({
                    marginRight: '8px',
                    height: '35px',
                    width: '1px',
                    backgroundColor: '#427a9d',
                  })}
                ></div>
                <Check size={24} />
              </div>
            }
            autoFocus={false}
            overrides={{
              BaseButton: {
                style: {
                  height: '40px',
                  color: customTheme.colors.white,
                  backgroundColor: '#3693bf',
                  outline: '#427a9d 1px solid',
                  fontWeight: '400',
                  textTransform: 'uppercase',
                  ':hover': {
                    backgroundColor: '#f6f6f6',
                    color: '#3693bf',
                  },
                },
                props: {
                  'data-testid': dataTestId,
                },
              },
            }}
            disabled={selectOptions.length > 0 && selectedValue.length === 0}
            onClick={() =>
              successModal(
                isLanguageSelector
                  ? { kind: 'languages', language: selectedValue?.[0]?.code }
                  : selector
                  ? { kind: 'reasons', reasonId, reasonName, reasonText }
                  : undefined,
              )
            }
          >
            {successBtnText}
          </ModalButton>
        </Block>
      </ModalFooter>
    </Block>
  );
};
